import React, { useMemo } from 'react';
import { palette } from '../utils/designTokens';
import { getDetectionLabel } from '../utils/signalUtils';
import {
  formatClassifierValue,
  getClassifierDefinition,
  getClassifierSubtypeDefinition,
} from '../services/classifiersApi';

const formatDetectionName = (row, definitionsIndex) => {
  const definition = getClassifierDefinition(definitionsIndex, row?.classifier_key || row?.key);
  if (definition?.name) return definition.name;
  const label = getDetectionLabel(row);
  if (label) return label;
  const key = row?.classifier_key || row?.key || '';
  if (!key) return 'Detection';
  return formatClassifierValue(key);
};

const detectionTone = (severity) => {
  const key = typeof severity === 'string' ? severity.toLowerCase() : '';
  if (key === 'critical' || key === 'high') return palette.status.danger;
  if (key === 'medium') return palette.status.warning;
  if (key === 'low') return palette.status.info;
  return palette.text.muted;
};

const detectionSubtypeText = (row, definitionsIndex) => {
  const definition = getClassifierDefinition(definitionsIndex, row?.classifier_key || row?.key);
  const subtypeDefinition = getClassifierSubtypeDefinition(definition, row);
  if (subtypeDefinition?.display_name) return subtypeDefinition.display_name;
  if (row?.display_label) return row.display_label;
  const subtype = row?.subtype || row?.signal_subtype || row?.primary_subtype || row?.details?.subtype || '';
  const matchEventKey = Array.isArray(row?.details?.matches)
    ? row.details.matches.find(match => match?.event_key)?.event_key
    : '';
  const eventKey = row?.event_key || row?.primary_event_key || row?.details?.event_key || matchEventKey || '';
  return [subtype, eventKey].filter(Boolean).map(formatClassifierValue).join(' / ');
};

const detectionSubtypeDescription = (row, definitionsIndex) => {
  const definition = getClassifierDefinition(definitionsIndex, row?.classifier_key || row?.key);
  const subtypeDefinition = getClassifierSubtypeDefinition(definition, row);
  return subtypeDefinition?.description || definition?.description || '';
};

const detectionEvidenceSpanId = (row) => {
  const evidenceIds = Array.isArray(row?.evidence_span_ids) ? row.evidence_span_ids : [];
  return row?.primary_span_id || evidenceIds[0] || '';
};

const detectionRowKey = (row) => row?.id || `${row?.classifier_key || 'detection'}:${row?.classifier_version || 'v'}`;

const rcaEnabledFor = (row, rcaEnabledKeys) => {
  const key = row?.classifier_key || row?.key || '';
  if (!key || !rcaEnabledKeys) return false;
  if (typeof rcaEnabledKeys.has === 'function') return rcaEnabledKeys.has(key);
  return Array.isArray(rcaEnabledKeys) ? rcaEnabledKeys.includes(key) : false;
};

const SignalCard = ({ row, definitionsIndex, onEvidenceClick, onRcaClick, rcaEnabledKeys, rcaLoadingKey }) => {
  const severity = row?.severity || 'detected';
  const tone = detectionTone(severity);
  const subtype = detectionSubtypeText(row, definitionsIndex) || 'Evidence span';
  const subtypeDescription = detectionSubtypeDescription(row, definitionsIndex);
  const evidenceSpanId = detectionEvidenceSpanId(row);
  const rowKey = detectionRowKey(row);
  const rcaEnabled = Boolean(onRcaClick) && rcaEnabledFor(row, rcaEnabledKeys);
  const rcaLoading = rcaLoadingKey === rowKey;

  const handleEvidenceClick = () => {
    if (evidenceSpanId) onEvidenceClick?.(evidenceSpanId);
  };

  const handleEvidenceKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (!evidenceSpanId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleEvidenceClick();
    }
  };

  return (
    <div
      role={evidenceSpanId ? 'button' : undefined}
      tabIndex={evidenceSpanId ? 0 : undefined}
      className={`h-11 min-w-[180px] max-w-[260px] shrink-0 rounded border px-2 py-1 text-left transition-colors hover:opacity-90 ${evidenceSpanId ? 'cursor-pointer' : ''}`}
      style={{
        borderColor: tone,
        backgroundColor: palette.neutrals.panelTertiary,
      }}
      onClick={handleEvidenceClick}
      onKeyDown={handleEvidenceKeyDown}
      title={subtypeDescription || (evidenceSpanId ? 'Jump to evidence span' : undefined)}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-xs font-semibold" style={{ color: palette.text.primary }}>
          {formatDetectionName(row, definitionsIndex)}
        </span>
        <span className="shrink-0 text-[10px] font-medium uppercase" style={{ color: tone }}>
          {severity}
        </span>
        {rcaEnabled && (
          <button
            type="button"
            className="ml-auto h-5 shrink-0 rounded border px-1.5 text-[10px] font-semibold uppercase disabled:cursor-wait disabled:opacity-70"
            style={{
              borderColor: palette.borders.subtle,
              color: palette.text.primary,
              backgroundColor: palette.neutrals.surfaceSubtle,
            }}
            disabled={rcaLoading}
            onClick={(event) => {
              event.stopPropagation();
              onRcaClick?.(row);
            }}
            title="View or generate RCA"
          >
            RCA
          </button>
        )}
      </div>
      <div className="truncate text-[10px]" style={{ color: palette.text.muted }} title={subtype}>
        {subtype}
      </div>
    </div>
  );
};

export default function TraceSignalsStrip({
  detections = [],
  definitionsIndex = null,
  onEvidenceClick,
  onRcaClick,
  rcaEnabledKeys,
  rcaLoadingKey = '',
}) {
  const detectedRows = useMemo(
    () => (Array.isArray(detections) ? detections : []).filter(row => row?.status === 'detected' || row?.detected),
    [detections]
  );

  if (detectedRows.length === 0) return null;

  return (
    <div className="flex items-center gap-2 overflow-x-auto">
      {detectedRows.map((row, index) => (
        <SignalCard
          key={row.id || `${row.classifier_key || 'detection'}:${row.classifier_version || 'v'}:${index}`}
          row={row}
          definitionsIndex={definitionsIndex}
          onEvidenceClick={onEvidenceClick}
          onRcaClick={onRcaClick}
          rcaEnabledKeys={rcaEnabledKeys}
          rcaLoadingKey={rcaLoadingKey}
        />
      ))}
    </div>
  );
}
